/**
 * The two places the router tells the identity rule about the page load's
 * ENTRY (#1253; the rule is `nextEntry` in `identityShell.ts`, the state lives
 * in `TenantProvider`).
 *
 * Both are layout effects: they run at COMMIT (never for a render React threw
 * away) and before paint, so the decision they change never reaches the screen
 * in its old form.
 */
import { useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTenant } from '../hooks/useTenant'
import type { ShellKind } from './identityShell'

/**
 * Mounted inside each shell's ROUTED tree — past every gate that returns early
 * without the routes (session loading, signed out, landing not ready, the
 * verify-email redirect, the tenant-denied screen) — and BEFORE its `<Routes>`,
 * so it records the entry before a catch-all inside them judges it. The entry
 * is a page the person actually reached — however they got there: an in-app
 * "Go to drive" / "Open my Drive" into `/~/…` is the person choosing their own
 * drive, and counts like any other link to it.
 */
export function EntryCapture({ shell }: { shell: ShellKind }): null {
  const { captureEntry } = useTenant()
  const { pathname } = useLocation()
  useLayoutEffect(() => { captureEntry(pathname, shell) }, [captureEntry, pathname, shell])
  return null
}

/**
 * Mounted in each shell's catch-all route (`path="*"`): when it catches the
 * entry itself, no route served the path the load entered at — it is about to
 * be redirected to the landing, so it was no destination and must not count as
 * a choice. Runs before `<Navigate>` (a passive effect) moves the location.
 */
export function EntryUnroutedMark({ shell }: { shell: ShellKind }): null {
  const { markEntryUnrouted } = useTenant()
  const { pathname } = useLocation()
  useLayoutEffect(() => { markEntryUnrouted(pathname, shell) }, [markEntryUnrouted, pathname, shell])
  return null
}
